/*
 * Copyright (c) 2026 Gabriele Bonora. Tutti i diritti riservati.
 *
 * Software proprietario. Nessuna licenza d'uso, copia, modifica o distribuzione
 * e' concessa in assenza di autorizzazione scritta del titolare dei diritti.
 * Vedere il file LICENSE nella radice del progetto.
 */

/*
 * L'inventario di UN cliente azienda, visto dall'ufficio.
 *
 * Non e' il nostro magazzino (quello sta in Materiali): e' il magazzino del
 * cliente, che il cliente stesso tiene aggiornato dall'area riservata. Qui si
 * entra dalla riga del cliente in Utenti, e ogni movimento registrato da qui
 * resta firmato "assistenza" nel registro che il cliente vede.
 */
import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Boxes, Plus, Search, X, Edit2, AlertTriangle, History, ArrowLeft,
  ArrowDownCircle, ArrowUpCircle, ClipboardCheck, Archive, RotateCcw, Trash2,
} from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { inventarioAPI } from '../api/inventario';
import { useViewport } from '../hooks/useViewport';

const ACCENT = '#8B5CF6';

// numeric(12,3) in arrivo dal database: "3.000" si mostra "3", "0.500" si mostra "0,5".
function qta(v) {
  const n = parseFloat(v ?? 0);
  if (!Number.isFinite(n)) return '0';
  return n.toFixed(3).replace(/\.?0+$/, '').replace('.', ',') || '0';
}

/*
 * Lo stesso controllo che fa il backend, ripetuto qui per dirlo MENTRE si scrive
 * invece che dopo aver premuto Salva. Il backend resta l'autorita'.
 *
 * Non usa parseFloat apposta: parseFloat("3abc") vale 3, e salverebbe in
 * silenzio un numero che nessuno ha scritto.
 */
function erroreNumero(v) {
  const t = String(v ?? '').trim();
  if (t === '') return null;
  if (!/^\d+([.,]\d+)?$/.test(t)) return 'Solo numeri: per esempio 12 oppure 12,5';
  return null;
}

function dataOra(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleString('it-IT', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

const TIPO_LABEL = {
  creazione: 'Giacenza iniziale',
  carico: 'Carico',
  scarico: 'Scarico',
  rettifica: 'Rettifica da conteggio',
};

export default function InventarioCliente() {
  const { clienteId } = useParams();
  const navigate = useNavigate();
  const { isCompact } = useViewport();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [filtroCategoria, setFiltroCategoria] = useState('');
  // Un solo filtro alla volta fra i tre riquadri: 'tutti' | 'sotto_scorta' | 'esauriti'.
  const [filtro, setFiltro] = useState('tutti');
  const [mostraArchiviati, setMostraArchiviati] = useState(false);
  const [inModifica, setInModifica] = useState(null);
  const [inMovimento, setInMovimento] = useState(null);
  const [inStorico, setInStorico] = useState(null);
  const [daEliminare, setDaEliminare] = useState(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['inventario', clienteId, search, filtroCategoria, filtro, mostraArchiviati],
    queryFn: () => inventarioAPI.list(clienteId, {
      q: search || undefined,
      categoria: filtroCategoria || undefined,
      attivo: mostraArchiviati ? undefined : 'true',
      sotto_scorta: filtro === 'sotto_scorta' ? 'true' : undefined,
      esauriti: filtro === 'esauriti' ? 'true' : undefined,
    }),
    retry: false,
  });
  const articoli = data?.articoli ?? [];

  const { data: categorie = [] } = useQuery({
    queryKey: ['inventario-categorie', clienteId],
    queryFn: () => inventarioAPI.categorie(clienteId),
    retry: false,
  });

  const { data: riepilogo } = useQuery({
    queryKey: ['inventario-riepilogo', clienteId],
    queryFn: () => inventarioAPI.riepilogo(clienteId),
    retry: false,
  });

  const aggiorna = () => {
    queryClient.invalidateQueries({ queryKey: ['inventario'] });
    queryClient.invalidateQueries({ queryKey: ['inventario-riepilogo'] });
    queryClient.invalidateQueries({ queryKey: ['inventario-categorie'] });
  };

  const archiviaMutation = useMutation({
    mutationFn: ({ id, attivo }) => inventarioAPI.archivia(id, attivo),
    onSuccess: aggiorna,
  });

  // Clic su un riquadro: accende quel filtro, o lo spegne se era gia' acceso.
  const cambiaFiltro = (quale) => setFiltro((f) => (f === quale ? 'tutti' : quale));

  // Un cliente privato, o un id che non e' di un cliente: il backend risponde 400
  // e qui si dice perche', invece di mostrare una tabella vuota.
  if (error) {
    return (
      <AdminLayout title="Inventario">
        <div style={styles.empty}>
          <Boxes size={40} color="#9CA3AF" />
          <p>{error.response?.data?.error || 'Inventario non disponibile per questo utente.'}</p>
          <button style={styles.secondaryBtn} onClick={() => navigate('/utenti')}>Torna agli utenti</button>
        </div>
      </AdminLayout>
    );
  }

  const titolo = data?.cliente?.denominazione ? `Inventario — ${data.cliente.denominazione}` : 'Inventario';

  return (
    <AdminLayout title={titolo}>
      <Link to="/utenti" style={styles.backLink}><ArrowLeft size={15} /> Utenti</Link>

      {/* I tre riquadri sono anche i filtri: dopo aver letto "3 sotto scorta" il
          gesto naturale e' cliccarci sopra per vedere quali. */}
      <div style={styles.statsRow}>
        <button
          type="button"
          onClick={() => setFiltro('tutti')}
          style={{ ...styles.statCard, ...(filtro === 'tutti' ? styles.statCardAttiva : {}) }}
        >
          <div style={styles.statLabel}>Articoli</div>
          <div style={styles.statValue}>{riepilogo?.articoli ?? '—'}</div>
          <div style={styles.statHint}>{filtro === 'tutti' ? 'stai vedendo tutti' : 'vedi tutti'}</div>
        </button>
        <button
          type="button"
          onClick={() => cambiaFiltro('sotto_scorta')}
          style={{ ...styles.statCard, ...(filtro === 'sotto_scorta' ? styles.statCardAttivaRossa : {}) }}
        >
          <div style={styles.statLabel}>Sotto scorta</div>
          <div style={{ ...styles.statValue, color: riepilogo?.sotto_scorta ? '#DC2626' : '#111827' }}>
            {riepilogo?.sotto_scorta ?? '—'}
          </div>
          <div style={styles.statHint}>{filtro === 'sotto_scorta' ? 'clicca per togliere' : 'clicca per vedere quali'}</div>
        </button>
        <button
          type="button"
          onClick={() => cambiaFiltro('esauriti')}
          style={{ ...styles.statCard, ...(filtro === 'esauriti' ? styles.statCardAttivaArancio : {}) }}
        >
          <div style={styles.statLabel}>Esauriti</div>
          <div style={{ ...styles.statValue, color: riepilogo?.esauriti ? '#D97706' : '#111827' }}>
            {riepilogo?.esauriti ?? '—'}
          </div>
          <div style={styles.statHint}>{filtro === 'esauriti' ? 'clicca per togliere' : 'clicca per vedere quali'}</div>
        </button>
      </div>

      <div style={styles.toolbar}>
        <div style={styles.searchBox}>
          <Search size={16} color="#9CA3AF" />
          <input
            style={styles.searchInput}
            placeholder="Cerca per nome, codice, ubicazione..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && <button style={styles.clearBtn} onClick={() => setSearch('')}><X size={14} /></button>}
        </div>
        <select style={styles.select} value={filtroCategoria} onChange={(e) => setFiltroCategoria(e.target.value)}>
          <option value="">Tutte le categorie</option>
          {categorie.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <button
          style={{ ...styles.chip, ...(mostraArchiviati ? styles.chipAttivo : {}) }}
          onClick={() => setMostraArchiviati((v) => !v)}
        >
          <Archive size={14} /> Archiviati
        </button>
        <button style={styles.primaryBtn} onClick={() => setInModifica('nuovo')}>
          <Plus size={16} /> Nuovo articolo
        </button>
      </div>

      <div style={styles.tableWrapper}>
        {isLoading ? (
          <div style={styles.empty}>Caricamento...</div>
        ) : articoli.length === 0 ? (
          <div style={styles.empty}>
            <Boxes size={40} color="#9CA3AF" />
            <p>{search || filtroCategoria || filtro !== 'tutti'
              ? 'Nessun articolo con questi filtri.'
              : 'Questo cliente non ha ancora articoli in inventario.'}</p>
          </div>
        ) : isCompact ? (
          // Su schermo stretto la tabella non ci sta: stesse informazioni in card.
          <div style={styles.cardList}>
            {articoli.map((a) => (
              <ArticoloCard
                key={a.id}
                articolo={a}
                onMovimento={(tipo) => setInMovimento({ articolo: a, tipo })}
                onStorico={() => setInStorico(a)}
                onModifica={() => setInModifica(a)}
                onArchivia={() => archiviaMutation.mutate({ id: a.id, attivo: !a.attivo })}
                onElimina={() => setDaEliminare(a)}
              />
            ))}
          </div>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr style={styles.thead}>
                <th style={styles.th}>Articolo</th>
                <th style={styles.th}>Categoria</th>
                <th style={styles.th}>Ubicazione</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Giacenza</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Scorta min.</th>
                <th style={styles.th}>Azioni</th>
              </tr>
            </thead>
            <tbody>
              {articoli.map((a) => {
                const sottoScorta = parseFloat(a.quantita_minima) > 0 && parseFloat(a.quantita) <= parseFloat(a.quantita_minima);
                return (
                  <tr key={a.id} style={{ ...styles.tr, ...(a.attivo ? {} : styles.trArchiviata) }}>
                    <td style={styles.td}>
                      <strong>{a.nome}</strong>
                      {a.codice && <span style={styles.codice}>{a.codice}</span>}
                      {!a.attivo && <span style={styles.archiviatoTag}>archiviato</span>}
                      {a.descrizione && <div style={styles.descrizione}>{a.descrizione}</div>}
                    </td>
                    <td style={styles.td}>{a.categoria || '—'}</td>
                    <td style={styles.td}>{a.ubicazione || '—'}</td>
                    <td style={{ ...styles.td, textAlign: 'right' }}>
                      <span style={{ fontWeight: 700, color: sottoScorta ? '#DC2626' : '#111827' }}>
                        {qta(a.quantita)}
                      </span>
                      <span style={styles.unita}> {a.unita_misura}</span>
                      {sottoScorta && (
                        <div style={styles.sottoScortaTag}><AlertTriangle size={12} /> sotto scorta</div>
                      )}
                    </td>
                    <td style={{ ...styles.td, textAlign: 'right', color: '#6B7280' }}>
                      {parseFloat(a.quantita_minima) > 0 ? qta(a.quantita_minima) : '—'}
                    </td>
                    <td style={styles.td}>
                      <div style={styles.azioni}>
                        {a.attivo && (
                          <>
                            <button style={styles.actionBtn} title="Carico" onClick={() => setInMovimento({ articolo: a, tipo: 'carico' })}>
                              <ArrowDownCircle size={15} color="#059669" />
                            </button>
                            <button style={styles.actionBtn} title="Scarico" onClick={() => setInMovimento({ articolo: a, tipo: 'scarico' })}>
                              <ArrowUpCircle size={15} color="#DC2626" />
                            </button>
                            <button style={styles.actionBtn} title="Rettifica da conteggio" onClick={() => setInMovimento({ articolo: a, tipo: 'rettifica' })}>
                              <ClipboardCheck size={15} />
                            </button>
                          </>
                        )}
                        <button style={styles.actionBtn} title="Movimenti" onClick={() => setInStorico(a)}>
                          <History size={15} />
                        </button>
                        {a.attivo && (
                          <button style={styles.actionBtn} title="Modifica" onClick={() => setInModifica(a)}>
                            <Edit2 size={15} />
                          </button>
                        )}
                        <button
                          style={styles.actionBtn}
                          title={a.attivo ? 'Archivia' : 'Ripristina'}
                          onClick={() => archiviaMutation.mutate({ id: a.id, attivo: !a.attivo })}
                        >
                          {a.attivo ? <Archive size={15} /> : <RotateCcw size={15} />}
                        </button>
                        <button
                          style={styles.actionBtnRossa}
                          title="Elimina definitivamente"
                          onClick={() => setDaEliminare(a)}
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {inModifica && (
        <ArticoloFormModal
          clienteId={clienteId}
          articolo={inModifica === 'nuovo' ? null : inModifica}
          categorie={categorie}
          onClose={() => setInModifica(null)}
          onSaved={() => { setInModifica(null); aggiorna(); }}
        />
      )}

      {inMovimento && (
        <MovimentoModal
          articolo={inMovimento.articolo}
          tipoIniziale={inMovimento.tipo}
          onClose={() => setInMovimento(null)}
          onSaved={() => { setInMovimento(null); aggiorna(); }}
        />
      )}

      {inStorico && <StoricoModal articolo={inStorico} onClose={() => setInStorico(null)} />}

      {daEliminare && (
        <EliminaModal
          articolo={daEliminare}
          onClose={() => setDaEliminare(null)}
          onDeleted={() => { setDaEliminare(null); aggiorna(); }}
        />
      )}
    </AdminLayout>
  );
}

// Versione a card della riga, per schermi stretti.
function ArticoloCard({ articolo: a, onMovimento, onStorico, onModifica, onArchivia, onElimina }) {
  const sottoScorta = parseFloat(a.quantita_minima) > 0 && parseFloat(a.quantita) <= parseFloat(a.quantita_minima);
  return (
    <div style={{ ...styles.card, ...(a.attivo ? {} : styles.trArchiviata) }}>
      <div style={styles.cardTop}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700 }}>{a.nome}</div>
          <div style={styles.cardMeta}>
            {a.codice && <span style={styles.codice}>{a.codice}</span>}
            {a.categoria && <span>{a.categoria}</span>}
            {a.ubicazione && <span>{a.ubicazione}</span>}
            {!a.attivo && <span style={styles.archiviatoTag}>archiviato</span>}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '1.25rem', fontWeight: 700, color: sottoScorta ? '#DC2626' : '#111827' }}>{qta(a.quantita)}</div>
          <div style={styles.unita}>{a.unita_misura}</div>
        </div>
      </div>
      {sottoScorta && <div style={styles.sottoScortaTag}><AlertTriangle size={12} /> sotto scorta ({qta(a.quantita_minima)})</div>}
      <div style={styles.azioni}>
        {a.attivo && (
          <>
            <button style={styles.actionBtn} onClick={() => onMovimento('carico')}><ArrowDownCircle size={15} color="#059669" /> Carico</button>
            <button style={styles.actionBtn} onClick={() => onMovimento('scarico')}><ArrowUpCircle size={15} color="#DC2626" /> Scarico</button>
            <button style={styles.actionBtn} onClick={() => onMovimento('rettifica')}><ClipboardCheck size={15} /> Conteggio</button>
          </>
        )}
        <button style={styles.actionBtn} onClick={onStorico}><History size={15} /> Movimenti</button>
        {a.attivo && <button style={styles.actionBtn} onClick={onModifica}><Edit2 size={15} /> Modifica</button>}
        <button style={styles.actionBtn} onClick={onArchivia}>
          {a.attivo ? <><Archive size={15} /> Archivia</> : <><RotateCcw size={15} /> Ripristina</>}
        </button>
        <button style={styles.actionBtnRossa} onClick={onElimina}><Trash2 size={15} /> Elimina</button>
      </div>
    </div>
  );
}

// ============================================================
// Anagrafica. La giacenza si inserisce solo alla creazione: dopo cambia
// esclusivamente con un movimento, che resta firmato nel registro.
// ============================================================
function ArticoloFormModal({ clienteId, articolo, categorie, onClose, onSaved }) {
  const nuovo = !articolo;
  const [form, setForm] = useState({
    codice: articolo?.codice || '',
    nome: articolo?.nome || '',
    descrizione: articolo?.descrizione || '',
    categoria: articolo?.categoria || '',
    unita_misura: articolo?.unita_misura || 'pz',
    quantita: '',
    quantita_minima: articolo ? qta(articolo.quantita_minima) : '',
    ubicazione: articolo?.ubicazione || '',
    note: articolo?.note || '',
  });
  const [errore, setErrore] = useState('');

  // Calcolati a ogni render: l'avviso compare mentre si scrive, sotto al campo.
  const errQuantita = nuovo ? erroreNumero(form.quantita) : null;
  const errMinima = erroreNumero(form.quantita_minima);

  const salva = useMutation({
    mutationFn: (payload) => (nuovo
      ? inventarioAPI.crea(clienteId, payload)
      : inventarioAPI.modifica(articolo.id, payload)),
    onSuccess: onSaved,
    onError: (e) => setErrore(e.response?.data?.error || 'Errore durante il salvataggio'),
  });

  const onSubmit = (e) => {
    e.preventDefault();
    setErrore('');
    if (!form.nome.trim()) return setErrore("Il nome e' obbligatorio");
    if (errQuantita || errMinima) return setErrore('Correggi i campi segnati in rosso');
    const payload = { ...form };
    if (!nuovo) delete payload.quantita;
    salva.mutate(payload);
  };

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <form style={styles.modal} onClick={(e) => e.stopPropagation()} onSubmit={onSubmit}>
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>{nuovo ? 'Nuovo articolo' : 'Modifica articolo'}</h2>
          <button type="button" style={styles.iconBtn} onClick={onClose}><X size={18} /></button>
        </div>

        <div style={styles.modalBody}>
          <label style={styles.label}>Nome *</label>
          <input style={styles.input} value={form.nome} autoFocus
            onChange={(e) => setForm({ ...form, nome: e.target.value })} />

          <div style={styles.row}>
            <div style={styles.col}>
              <label style={styles.label}>Codice</label>
              <input style={styles.input} value={form.codice} placeholder="facoltativo"
                onChange={(e) => setForm({ ...form, codice: e.target.value })} />
            </div>
            <div style={styles.col}>
              <label style={styles.label}>Unita di misura</label>
              <input style={styles.input} value={form.unita_misura} placeholder="pz, m, kg, l"
                onChange={(e) => setForm({ ...form, unita_misura: e.target.value })} />
            </div>
          </div>

          <label style={styles.label}>Categoria</label>
          <input style={styles.input} list="categorie-inventario-admin" value={form.categoria}
            onChange={(e) => setForm({ ...form, categoria: e.target.value })} placeholder="facoltativa" />
          <datalist id="categorie-inventario-admin">
            {categorie.map((c) => <option key={c} value={c} />)}
          </datalist>

          <div style={styles.row}>
            {nuovo && (
              <div style={styles.col}>
                <label style={styles.label}>Quantita iniziale</label>
                <input style={{ ...styles.input, ...(errQuantita ? styles.inputErrato : {}) }}
                  inputMode="decimal" value={form.quantita} placeholder="0"
                  onChange={(e) => setForm({ ...form, quantita: e.target.value })} />
                {errQuantita
                  ? <div style={styles.erroreCampo}><AlertTriangle size={13} /> {errQuantita}</div>
                  : <div style={styles.hint}>Se la lasci vuota parte da zero.</div>}
              </div>
            )}
            <div style={styles.col}>
              <label style={styles.label}>Scorta minima</label>
              <input style={{ ...styles.input, ...(errMinima ? styles.inputErrato : {}) }}
                inputMode="decimal" value={form.quantita_minima} placeholder="0"
                onChange={(e) => setForm({ ...form, quantita_minima: e.target.value })} />
              {errMinima && <div style={styles.erroreCampo}><AlertTriangle size={13} /> {errMinima}</div>}
            </div>
          </div>

          <label style={styles.label}>Ubicazione</label>
          <input style={styles.input} value={form.ubicazione} placeholder="Es. Scaffale B, ripiano 2"
            onChange={(e) => setForm({ ...form, ubicazione: e.target.value })} />

          <label style={styles.label}>Descrizione</label>
          <input style={styles.input} value={form.descrizione}
            onChange={(e) => setForm({ ...form, descrizione: e.target.value })} />

          <label style={styles.label}>Note</label>
          <textarea style={{ ...styles.input, minHeight: '70px', resize: 'vertical' }} value={form.note}
            onChange={(e) => setForm({ ...form, note: e.target.value })} />

          {!nuovo && (
            <div style={styles.hint}>
              La giacenza ({qta(articolo.quantita)} {articolo.unita_misura}) non si modifica da qui:
              registra un carico, uno scarico o una rettifica.
            </div>
          )}

          {errore && <div style={styles.errore}>{errore}</div>}
        </div>

        <div style={styles.modalActions}>
          <button type="button" style={styles.secondaryBtn} onClick={onClose}>Annulla</button>
          <button
            type="submit"
            style={{ ...styles.primaryBtn, ...(errQuantita || errMinima ? styles.btnDisabilitato : {}) }}
            disabled={salva.isPending || Boolean(errQuantita || errMinima)}
          >
            {salva.isPending ? 'Salvataggio...' : 'Salva'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ============================================================
// Il movimento.
// ============================================================
function MovimentoModal({ articolo, tipoIniziale, onClose, onSaved }) {
  const [tipo, setTipo] = useState(tipoIniziale);
  const [quantita, setQuantita] = useState('');
  const [nota, setNota] = useState('');
  const [errore, setErrore] = useState('');

  const registra = useMutation({
    mutationFn: (payload) => inventarioAPI.movimento(articolo.id, payload),
    onSuccess: onSaved,
    onError: (e) => setErrore(e.response?.data?.error || 'Errore durante il salvataggio'),
  });

  const errQuantita = erroreNumero(quantita);
  const attuale = parseFloat(articolo.quantita);
  const inserita = parseFloat(String(quantita).replace(',', '.'));
  // L'anteprima si calcola solo su un numero pulito, altrimenti direbbe bugie.
  const risultante = !errQuantita && String(quantita).trim() !== '' && Number.isFinite(inserita)
    ? (tipo === 'carico' ? attuale + inserita : tipo === 'scarico' ? attuale - inserita : inserita)
    : null;

  const onSubmit = (e) => {
    e.preventDefault();
    setErrore('');
    if (errQuantita) return setErrore(errQuantita);
    if (String(quantita).trim() === '') return setErrore('Scrivi una quantita');
    if (tipo !== 'rettifica' && inserita === 0) return setErrore('Scrivi una quantita maggiore di zero');
    registra.mutate({ tipo, quantita, nota });
  };

  const TIPI = [
    { id: 'carico', label: 'Carico', desc: 'Materiale entrato' },
    { id: 'scarico', label: 'Scarico', desc: 'Materiale usato' },
    { id: 'rettifica', label: 'Conteggio', desc: 'Contato allo scaffale' },
  ];

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <form style={styles.modal} onClick={(e) => e.stopPropagation()} onSubmit={onSubmit}>
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>{articolo.nome}</h2>
          <button type="button" style={styles.iconBtn} onClick={onClose}><X size={18} /></button>
        </div>

        <div style={styles.modalBody}>
          <div style={styles.giacenzaAttuale}>
            In giacenza ora: <strong>{qta(articolo.quantita)} {articolo.unita_misura}</strong>
          </div>

          <div style={styles.tipoRow}>
            {TIPI.map((t) => (
              <button key={t.id} type="button" onClick={() => { setTipo(t.id); setErrore(''); }}
                style={{ ...styles.tipoBtn, ...(tipo === t.id ? styles.tipoBtnAttivo : {}) }}>
                <span style={styles.tipoLabel}>{t.label}</span>
                <span style={styles.tipoDesc}>{t.desc}</span>
              </button>
            ))}
          </div>

          <label style={styles.label}>
            {tipo === 'rettifica' ? `Quantita contata (${articolo.unita_misura})` : `Quantita (${articolo.unita_misura})`}
          </label>
          <input style={{ ...styles.input, fontSize: '1.2rem', fontWeight: 600, ...(errQuantita ? styles.inputErrato : {}) }}
            inputMode="decimal" value={quantita} autoFocus placeholder="0"
            onChange={(e) => { setQuantita(e.target.value); setErrore(''); }} />

          {errQuantita ? (
            <div style={styles.erroreCampo}><AlertTriangle size={13} /> {errQuantita}</div>
          ) : risultante !== null ? (
            <div style={{ ...styles.hint, color: risultante < 0 ? '#DC2626' : '#6B7280' }}>
              {risultante < 0
                ? `Non ci sono abbastanza pezzi: in giacenza ce ne sono ${qta(articolo.quantita)}.`
                : `Dopo il movimento: ${qta(risultante)} ${articolo.unita_misura}`}
            </div>
          ) : null}

          <label style={styles.label}>Nota</label>
          <input style={styles.input} value={nota} maxLength={255}
            placeholder="Il cliente la vede nel registro"
            onChange={(e) => setNota(e.target.value)} />

          <div style={styles.hint}>
            Il movimento resta firmato con il tuo nome e la dicitura "assistenza"
            nel registro che il cliente puo' leggere.
          </div>

          {errore && <div style={styles.errore}>{errore}</div>}
        </div>

        <div style={styles.modalActions}>
          <button type="button" style={styles.secondaryBtn} onClick={onClose}>Annulla</button>
          <button
            type="submit"
            style={{ ...styles.primaryBtn, ...(errQuantita ? styles.btnDisabilitato : {}) }}
            disabled={registra.isPending || Boolean(errQuantita)}
          >
            {registra.isPending ? 'Registrazione...' : 'Registra'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ============================================================
// Cancellazione definitiva.
//
// Non usa window.confirm: una finestra del browser direbbe una riga sola e non
// potrebbe dire la cosa che conta, cioe' che se ne va anche il registro. E il
// registro e' il motivo per cui questa funzione esiste.
// ============================================================
function EliminaModal({ articolo, onClose, onDeleted }) {
  const [errore, setErrore] = useState('');
  const elimina = useMutation({
    mutationFn: () => inventarioAPI.elimina(articolo.id),
    onSuccess: onDeleted,
    onError: (e) => setErrore(e.response?.data?.error || 'Errore durante la cancellazione'),
  });

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={{ ...styles.modal, maxWidth: '440px' }} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>Eliminare "{articolo.nome}"?</h2>
          <button type="button" style={styles.iconBtn} onClick={onClose}><X size={18} /></button>
        </div>
        <div style={styles.modalBody}>
          <div style={styles.avvisoRosso}>
            <Trash2 size={16} />
            <div>
              L'articolo sparisce dall'inventario del cliente <strong>insieme a tutto il
              suo registro dei movimenti</strong>. Non si torna indietro, e il cliente
              non lo ritrovera' nemmeno fra gli archiviati.
            </div>
          </div>
          <div style={styles.hint}>
            Per toglierlo dall'elenco conservandone la storia usa <strong>Archivia</strong>.
          </div>
          {errore && <div style={styles.errore}>{errore}</div>}
        </div>
        <div style={styles.modalActions}>
          <button type="button" style={styles.secondaryBtn} onClick={onClose}>Annulla</button>
          <button type="button" style={styles.dangerBtn} onClick={() => elimina.mutate()} disabled={elimina.isPending}>
            {elimina.isPending ? 'Eliminazione...' : 'Elimina definitivamente'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Lo storico.
// ============================================================
function StoricoModal({ articolo, onClose }) {
  const { data: movimenti = [], isLoading } = useQuery({
    queryKey: ['inventario-movimenti', articolo.id],
    queryFn: () => inventarioAPI.movimenti(articolo.id),
  });

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>Movimenti — {articolo.nome}</h2>
          <button type="button" style={styles.iconBtn} onClick={onClose}><X size={18} /></button>
        </div>
        <div style={styles.modalBody}>
          {isLoading ? (
            <div style={styles.empty}>Caricamento...</div>
          ) : movimenti.length === 0 ? (
            <div style={styles.hint}>Nessun movimento registrato.</div>
          ) : (
            <div style={styles.storicoList}>
              {movimenti.map((m) => {
                const delta = parseFloat(m.delta);
                return (
                  <div key={m.id} style={styles.storicoRiga}>
                    <div style={styles.storicoTop}>
                      <span style={styles.storicoTipo}>{TIPO_LABEL[m.tipo] || m.tipo}</span>
                      <span style={{ ...styles.storicoDelta, color: delta >= 0 ? '#059669' : '#DC2626' }}>
                        {delta > 0 ? '+' : ''}{qta(delta)} {articolo.unita_misura}
                      </span>
                    </div>
                    <div style={styles.storicoDettaglio}>
                      da {qta(m.quantita_precedente)} a {qta(m.quantita_risultante)} {articolo.unita_misura}
                    </div>
                    {m.nota && <div style={styles.storicoNota}>{m.nota}</div>}
                    <div style={styles.storicoFirma}>
                      {dataOra(m.created_at)} — {m.utente_nome || 'utente rimosso'}
                      {m.utente_ruolo === 'admin' ? ' (assistenza)' : ' (cliente)'}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div style={styles.modalActions}>
          <button type="button" style={styles.secondaryBtn} onClick={onClose}>Chiudi</button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  backLink: { display: 'inline-flex', alignItems: 'center', gap: '0.3rem', color: '#6B7280', textDecoration: 'none', fontSize: '0.85rem', marginBottom: '0.85rem' },

  statsRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.75rem', marginBottom: '1rem' },
  statCard: { backgroundColor: 'white', borderRadius: '12px', padding: '0.9rem', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', border: '2px solid transparent', textAlign: 'left', cursor: 'pointer', font: 'inherit', minHeight: '44px', display: 'block', width: '100%' },
  statCardAttiva: { borderColor: ACCENT, backgroundColor: ACCENT + '0D' },
  statCardAttivaRossa: { borderColor: '#DC2626', backgroundColor: '#FEF2F2' },
  statCardAttivaArancio: { borderColor: '#D97706', backgroundColor: '#FFFBEB' },
  statLabel: { fontSize: '0.75rem', color: '#6B7280' },
  statValue: { fontSize: '1.5rem', fontWeight: 700, marginTop: '0.15rem' },
  statHint: { fontSize: '0.68rem', color: '#9CA3AF', marginTop: '0.1rem' },

  toolbar: { display: 'flex', gap: '0.6rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '1rem' },
  searchBox: { display: 'flex', alignItems: 'center', gap: '0.5rem', backgroundColor: 'white', border: '1px solid #E5E7EB', borderRadius: '10px', padding: '0.5rem 0.7rem', flex: '1 1 220px', minWidth: 0 },
  searchInput: { border: 'none', outline: 'none', fontSize: '0.9rem', flex: 1, minWidth: 0 },
  clearBtn: { border: 'none', background: 'none', cursor: 'pointer', color: '#9CA3AF', display: 'flex', padding: 0 },
  select: { padding: '0.55rem 0.7rem', border: '1px solid #E5E7EB', borderRadius: '10px', fontSize: '0.9rem', backgroundColor: 'white', minHeight: '40px' },
  chip: { display: 'inline-flex', alignItems: 'center', gap: '0.35rem', padding: '0.5rem 0.75rem', border: '1px solid #E5E7EB', borderRadius: '10px', backgroundColor: 'white', color: '#374151', fontSize: '0.85rem', cursor: 'pointer', minHeight: '40px' },
  chipAttivo: { backgroundColor: ACCENT + '15', borderColor: ACCENT, color: ACCENT, fontWeight: 600 },
  primaryBtn: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem', padding: '0.55rem 1rem', backgroundColor: ACCENT, color: 'white', border: 'none', borderRadius: '10px', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer', minHeight: '40px' },
  secondaryBtn: { padding: '0.55rem 1rem', backgroundColor: '#F3F4F6', color: '#374151', border: 'none', borderRadius: '10px', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer', minHeight: '40px' },
  dangerBtn: { padding: '0.55rem 1rem', backgroundColor: '#DC2626', color: 'white', border: 'none', borderRadius: '10px', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer', minHeight: '40px' },
  btnDisabilitato: { opacity: 0.45, cursor: 'not-allowed' },

  tableWrapper: { backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse' },
  thead: { backgroundColor: '#F9FAFB' },
  th: { padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.03em', whiteSpace: 'nowrap' },
  tr: { borderTop: '1px solid #F3F4F6' },
  trArchiviata: { opacity: 0.55 },
  td: { padding: '0.75rem 1rem', fontSize: '0.88rem', color: '#111827', verticalAlign: 'top' },
  codice: { fontFamily: 'monospace', backgroundColor: '#F3F4F6', padding: '0 0.35rem', borderRadius: '4px', fontSize: '0.78rem', marginLeft: '0.4rem' },
  archiviatoTag: { color: '#9CA3AF', fontStyle: 'italic', fontSize: '0.78rem', marginLeft: '0.4rem' },
  descrizione: { fontSize: '0.8rem', color: '#6B7280', marginTop: '0.2rem' },
  unita: { fontSize: '0.78rem', color: '#9CA3AF' },
  sottoScortaTag: { display: 'inline-flex', alignItems: 'center', gap: '0.25rem', color: '#DC2626', fontSize: '0.72rem', fontWeight: 600, marginTop: '0.2rem' },
  azioni: { display: 'flex', gap: '0.35rem', flexWrap: 'wrap' },
  actionBtn: { display: 'inline-flex', alignItems: 'center', gap: '0.3rem', padding: '0.4rem 0.55rem', backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '8px', fontSize: '0.8rem', color: '#374151', cursor: 'pointer', minHeight: '36px' },
  actionBtnRossa: { display: 'inline-flex', alignItems: 'center', gap: '0.3rem', padding: '0.4rem 0.55rem', backgroundColor: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '8px', fontSize: '0.8rem', color: '#B91C1C', cursor: 'pointer', minHeight: '36px' },
  empty: { padding: '3rem 1.5rem', textAlign: 'center', color: '#6B7280', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.6rem' },

  cardList: { display: 'flex', flexDirection: 'column', gap: '0.75rem', padding: '0.75rem' },
  card: { border: '1px solid #E5E7EB', borderRadius: '10px', padding: '0.85rem' },
  cardTop: { display: 'flex', gap: '0.75rem', alignItems: 'flex-start' },
  cardMeta: { display: 'flex', gap: '0.5rem', flexWrap: 'wrap', fontSize: '0.78rem', color: '#6B7280', marginTop: '0.15rem' },

  modalOverlay: { position: 'fixed', top: 0, bottom: 0, left: 0, right: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: '1rem' },
  modal: { backgroundColor: 'white', borderRadius: '12px', width: '100%', maxWidth: '520px', maxHeight: '90vh', overflow: 'auto', display: 'flex', flexDirection: 'column' },
  modalHeader: { padding: '1.1rem 1.25rem', borderBottom: '1px solid #E5E7EB', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem' },
  modalTitle: { margin: 0, fontSize: '1.05rem', fontWeight: 700 },
  modalBody: { padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.3rem' },
  modalActions: { padding: '1rem 1.25rem', borderTop: '1px solid #E5E7EB', display: 'flex', justifyContent: 'flex-end', gap: '0.6rem' },
  iconBtn: { border: 'none', background: 'none', cursor: 'pointer', color: '#6B7280', display: 'flex', padding: '0.25rem' },

  label: { fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginTop: '0.6rem' },
  input: { width: '100%', padding: '0.6rem 0.7rem', border: '1px solid #E5E7EB', borderRadius: '10px', fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' },
  inputErrato: { borderColor: '#DC2626', backgroundColor: '#FEF2F2' },
  row: { display: 'flex', gap: '0.75rem', flexWrap: 'wrap' },
  col: { flex: '1 1 140px', minWidth: 0 },
  hint: { fontSize: '0.78rem', color: '#6B7280', marginTop: '0.35rem' },
  erroreCampo: { display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.78rem', color: '#DC2626', fontWeight: 600, marginTop: '0.35rem' },
  errore: { marginTop: '0.75rem', padding: '0.6rem 0.75rem', backgroundColor: '#FEE2E2', color: '#991B1B', borderRadius: '8px', fontSize: '0.85rem' },
  avvisoRosso: { display: 'flex', gap: '0.6rem', alignItems: 'flex-start', padding: '0.75rem 0.85rem', backgroundColor: '#FEF2F2', border: '1px solid #FECACA', color: '#991B1B', borderRadius: '10px', fontSize: '0.88rem', lineHeight: 1.45 },

  giacenzaAttuale: { padding: '0.6rem 0.75rem', backgroundColor: '#F9FAFB', borderRadius: '8px', fontSize: '0.9rem', color: '#374151' },
  tipoRow: { display: 'flex', gap: '0.5rem', marginTop: '0.85rem', flexWrap: 'wrap' },
  tipoBtn: { flex: '1 1 100px', display: 'flex', flexDirection: 'column', gap: '0.15rem', padding: '0.6rem', border: '1px solid #E5E7EB', borderRadius: '10px', backgroundColor: 'white', cursor: 'pointer', textAlign: 'left', minHeight: '44px' },
  tipoBtnAttivo: { borderColor: ACCENT, backgroundColor: ACCENT + '10' },
  tipoLabel: { fontSize: '0.88rem', fontWeight: 700, color: '#111827' },
  tipoDesc: { fontSize: '0.72rem', color: '#6B7280' },

  storicoList: { display: 'flex', flexDirection: 'column', gap: '0.6rem' },
  storicoRiga: { padding: '0.7rem 0.8rem', backgroundColor: '#F9FAFB', borderRadius: '10px' },
  storicoTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem' },
  storicoTipo: { fontSize: '0.85rem', fontWeight: 700, color: '#111827' },
  storicoDelta: { fontSize: '0.9rem', fontWeight: 700 },
  storicoDettaglio: { fontSize: '0.78rem', color: '#6B7280', marginTop: '0.15rem' },
  storicoNota: { fontSize: '0.82rem', color: '#374151', marginTop: '0.3rem', fontStyle: 'italic' },
  storicoFirma: { fontSize: '0.72rem', color: '#9CA3AF', marginTop: '0.3rem' },
};
